import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, lastValueFrom, of } from 'rxjs';

import { Types } from '../../domain/types.domain';

import { AuthRepositoryInterface } from '../../domain/auth/interfaces/repositories/auth.repository.interface';
import { LoginByFormInput } from '../../domain/auth/dtos/login-by-form.input';
import { LoginByFormOutput } from '../../domain/auth/dtos/login-by-form.output';
import { LoginByIntegrationInput } from '../../domain/auth/dtos/login-by-integration.input';
import { LoginByIntegrationOutput } from '../../domain/auth/dtos/login-by-integration.output';
import { RecoverPasswordInput } from '../../domain/auth/dtos/recover-password.input';
import { VerifyChangePasswordTokenInput } from '../../domain/auth/dtos/verify-change-password-token.input';
import { VerifyUserPasswordInput } from '../../domain/auth/dtos/verify-user-password.input';
import { ResetPasswordInput } from '../../domain/auth/dtos/reset-password.input';
import { ResendAccountConfirmationInput } from '../../domain/auth/dtos/resend-account-confirmation.input';
import { VerifyCreateAccountTokenInput } from '../../domain/auth/dtos/verify-create-account-token.input';
import { VerifyCreateAccountTokenOutput } from '../../domain/auth/dtos/verify-create-account-token.output';
import { GetConfigByCityIdOutput } from '../../domain/auth/dtos/get-config-by-city-id.output';
import { UseAuthorizationCodeInput } from '../../domain/auth/dtos/use-authorization-code.input';
import { UseAuthorizationCodeOutput } from '../../domain/auth/dtos/use-authorization-code.output';
import { ValidateAccountConfirmationInput } from '../../domain/auth/dtos/validate-account-confirmation.input';
import { ValidateAccountConfirmationOutput } from '../../domain/auth/dtos/validate-account-confirmation.output';
import { GetPylonCitiesInput } from '../../domain/auth/dtos/get-pylon-cities.input';
import { GetPylonCitiesOutput } from '../../domain/auth/dtos/get-pylon-cities.output';
import { StartAccountCreationInput } from '../../domain/auth/dtos/start-account-creation.input';
import { StartAccountCreationOutput } from '../../domain/auth/dtos/start-account-creation.output';
import { CreatePersonalAccountInput } from '../../domain/auth/dtos/create-personal-account.input';
import { CreatePersonalAccountOutput } from '../../domain/auth/dtos/create-personal-account.output';
import { GovBrCompanyAccountInput } from '../../domain/auth/dtos/govbr-company-account.input';
import { GovBrCompanyAccountOutput } from '../../domain/auth/dtos/govbr-company-account.output';
import { CompleteRegistrationInput } from '../../domain/auth/dtos/complete-registration.input';
import { CompleteRegistrationOutput } from '../../domain/auth/dtos/complete-registration.output';
import { User } from '../../domain/user/entities/user.entity';

@Injectable()
export class AuthHttpRepository implements AuthRepositoryInterface {
  private readonly _http = inject(HttpClient);
  private readonly _settings = inject(Types.Settings);

  async loginByForm(input: LoginByFormInput): Promise<LoginByFormOutput> {
    return lastValueFrom(
      this._http.post<LoginByFormOutput>(
        `${this._settings.authenticationUrl.v1}/oauth/login`,
        // Contrato do authentication-api: { email, password, realm, cityId?, recaptchaReactive? }.
        {
          email: input.email,
          password: input.password,
          realm: input.connection,
          recaptchaReactive: input.recaptchaReactive ?? undefined,
        }
      )
    );
  }

  async loginByIntegration(
    input: LoginByIntegrationInput
  ): Promise<LoginByIntegrationOutput> {
    return lastValueFrom(
      this._http.post<LoginByIntegrationOutput>(
        `${this._settings.authenticationUrl.v1}/oauth/integration`,
        {
          ...input,
        }
      )
    );
  }

  async recoverPassword(input: RecoverPasswordInput): Promise<void> {
    return lastValueFrom(
      this._http.post<void>(
        `${this._settings.authenticationUrl.v1}/password/recover`,
        {
          ...input,
          uiLocales: input.uiLocales ?? 'pt-br',
        }
      )
    );
  }

  async verifyChangePasswordToken(
    input: VerifyChangePasswordTokenInput
  ): Promise<boolean> {
    return lastValueFrom(
      this._http.post<boolean>(
        `${this._settings.authenticationUrl.v1}/password/token/verify`,
        input
      )
    );
  }

  async verifyPassword(input: VerifyUserPasswordInput): Promise<boolean> {
    return lastValueFrom(
      this._http.post<boolean>(
        `${this._settings.authenticationUrl.v1}/password/verify`,
        input
      )
    );
  }

  async resetPassword(input: ResetPasswordInput): Promise<void> {
    return lastValueFrom(
      this._http.post<void>(
        `${this._settings.authenticationUrl.v1}/password/reset`,
        {
          ...input,
          uiLocales: input.uiLocales ?? 'pt-br',
        }
      )
    );
  }

  async resendAccountConfirmation(
    input: ResendAccountConfirmationInput
  ): Promise<boolean> {
    return lastValueFrom(
      this._http.post<boolean>(
        `${this._settings.authenticationUrl.v1}/send-account-confirmation`,
        input
      )
    );
  }

  async verifyCreateAccountToken(
    input: VerifyCreateAccountTokenInput
  ): Promise<VerifyCreateAccountTokenOutput> {
    return lastValueFrom(
      this._http.post<VerifyCreateAccountTokenOutput>(
        `${this._settings.authenticationUrl.v1}/verify-create-account-token`,
        input
      )
    );
  }

  async validateAccountConfirmation(
    input: ValidateAccountConfirmationInput
  ): Promise<ValidateAccountConfirmationOutput> {
    // APR-974: `POST /v1/validate-account-confirmation` (authentication-api) substitui o
    // `/api/v2/users/validateAccountConfirmation` do serviço legado. Mesmo contrato:
    // { userId, token } -> { success }; 403 `invalidAccountConfirmationToken` quando inválido.
    return lastValueFrom(
      this._http.post<ValidateAccountConfirmationOutput>(
        `${this._settings.authenticationUrl.v1}/validate-account-confirmation`,
        {
          userId: input.userId,
          token: input.token,
        }
      )
    );
  }

  async getConfigByCityId(input: number): Promise<GetConfigByCityIdOutput> {
    return lastValueFrom(
      this._http
        .get<GetConfigByCityIdOutput>(
          `${this._settings.authenticationUrl.v1}/auth-config/${input}`
        )
        .pipe(catchError(() => of({} as GetConfigByCityIdOutput)))
    );
  }

  async useAuthorizationCode(
    input: UseAuthorizationCodeInput
  ): Promise<UseAuthorizationCodeOutput> {
    return lastValueFrom(
      this._http.put<UseAuthorizationCodeOutput>(
        `${this._settings.authenticationUrl.v1}/authorization-code/use`,
        input
      )
    );
  }

  async getPylonCities(
    input: GetPylonCitiesInput
  ): Promise<GetPylonCitiesOutput> {
    return lastValueFrom(
      this._http.post<GetPylonCitiesOutput>(
        `${this._settings.authenticationUrl.v1}/oauth/pylon/cities`,
        input
      )
    );
  }

  async startAccountCreation(
    input: StartAccountCreationInput
  ): Promise<StartAccountCreationOutput> {
    return lastValueFrom(
      this._http.post<StartAccountCreationOutput>(
        `${this._settings.authenticationUrl.v1}/oauth/account-creation`,
        input
      )
    );
  }

  async createPersonalAccount(
    input: CreatePersonalAccountInput
  ): Promise<CreatePersonalAccountOutput> {
    return lastValueFrom(
      this._http.post<CreatePersonalAccountOutput>(
        `${this._settings.authenticationUrl.v1}/oauth/account-creation/personal`,
        input
      )
    );
  }

  async createCompanyAccount(
    input: GovBrCompanyAccountInput
  ): Promise<GovBrCompanyAccountOutput> {
    return lastValueFrom(
      this._http.post<GovBrCompanyAccountOutput>(
        `${this._settings.authenticationUrl.v1}/oauth/account-creation/company`,
        input
      )
    );
  }

  async completeRegistration(
    input: CompleteRegistrationInput
  ): Promise<CompleteRegistrationOutput> {
    return lastValueFrom(
      this._http.post<CompleteRegistrationOutput>(
        `${this._settings.authenticationUrl.v1}/oauth/complete-registration`,
        input
      )
    );
  }

  async getRegistrationCompletionAccount(registrationToken: string): Promise<User> {
    return lastValueFrom(
      this._http.post<User>(`${this._settings.authenticationUrl.v1}/oauth/complete-registration/account`, {
        registrationToken,
      })
    );
  }
}
